import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, signal, ViewChild, OnInit, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, UrlService, } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, ExecutionResult, FilterItem, IBudgetCodeFiscal, IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { Chart, ChartConfiguration, ChartEvent, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BudgetService, SegmentService } from "../../services";

@Component({
    selector: "general-ledger-budget-by-department-chart",
    templateUrl: "./budgetByDepartmentChart.tmpl.html",
    styleUrls: ["./budgetByDepartmentChart.component.scss"],
    standalone: false
})
export class BudgetByDepartmentChartComponent implements OnDestroy, ICustomViewComponent<ChartBindingObject>, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $segmentService = inject(SegmentService);
    private readonly $intentService = inject(IntentService);
    private readonly $accountBudgetService = inject(BudgetService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    readonly commands: ReadonlyArray<Command>;
    readonly customDataBinding: ChartBindingObject;
    private readonly _chartId: string;
    private _chartCriteria: IChartCriteria;
    private budgetCodeFiscals: Array<IBudgetCodeFiscal>;

    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get showSettings(): boolean
    {
        return (
            !this.chartCriteria.HideSettings &&
            this.chartCriteria?.Parameters?.filter(g => g.Hidden === false)?.length > 0
        );
    }

    get ChartTitle(): string
    {
        const groupBy = this.chartCriteria?.Parameters?.find(x => x.Id === "BudgetByDepartmentGroupById");
        const groupById = groupBy?.Value;
        const groupByDisplay = groupBy?.Options?.find(x => x.Value === groupById)?.Display;
        if (!groupByDisplay)
        {
            return "Budget Activity";
        }
        return "Budget Activity by " + groupByDisplay;
    }

    get ChartSubtitle(): string
    {
        const budgetCodeFiscalId = this.chartCriteria?.Parameters?.find(
            x => x.Id === "BudgetByDepartmentFiscal"
        )?.Value;
        if (!budgetCodeFiscalId)
        {
            return null;
        }
        if (this.budgetCodeFiscals)
        {
            const fiscal = this.budgetCodeFiscals.find(x => x.BudgetCodeFiscalId === budgetCodeFiscalId);
            return fiscal ? fiscal.FiscalName : null;
        }
        return null;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.$accountBudgetService.GetBudgetCodeFiscals().then((result: ExecutionResult<Array<IBudgetCodeFiscal>>) =>
        {
            this.budgetCodeFiscals = result.Result;
        });
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return !!this.chartCriteria?.ChartDatas && !!this.chartCriteria.ChartDatas[0].Data[0];
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (err: unknown) =>
                {
                    console.error("BudgetByDepartmentChartComponent | error", err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                const departmentId = criteria.Parameters.find(x => x.Id === "BudgetByDepartmentGroupById")?.Value;
                if (departmentId)
                {
                    this.$segmentService.GetSubSegmentValues(departmentId).then(result =>
                    {
                        const subDepartmentId = criteria.Parameters
                            .find(x => x.Id === "BudgetByDepartmentGroupBySubId");
                        const settingOptions: Array<SettingLookupOption> = [
                            {
                                Value: null,
                                Display: "Top 5 by Activity",
                            },
                            ...result.map(x =>
                            {
                                return {
                                    Value: x.Id,
                                    Display: x.Name,
                                };
                            }),
                        ];
                        if (settingOptions)
                        {
                            subDepartmentId.Options = settingOptions;
                        }
                        subDepartmentId.Value = null;
                    });
                }
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    updateChartConfiguration(): void
    {
        const labels = this.chartCriteria.ChartDatas[0].Labels;
        const chartColors = this.$chartService.getColors(2);

        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                maintainAspectRatio: true,
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: true,
                        position: "top",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: Array<TooltipItem<"bar">>): string | Array<string> =>
                            {
                                return labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | Array<string> =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    y: {
                        display: true,
                        grid: { display: true },
                        labels: labels,
                    },
                },
                datasets: {
                    bar: {
                        barPercentage: 0.8,
                    }
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _activeElements: Array<unknown>, chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }
                    const index = element.index;
                    const filter: Array<FilterItem> = [
                        {
                            Key: "groupValue",
                            Value: chart.options.plugins.legend.labels[index],
                        },
                    ];
                    const subset: ProfileSettingViewModel = this.chartCriteria?.Parameters?.find(
                        v => v.Id === "BudgetByDepartmentGroupBySubId"
                    );
                    if (subset?.Value)
                    {
                        const groupById = this.chartCriteria?.Parameters?.find(v => v.Id === "BudgetByDepartmentGroupById");
                        const groupBy = groupById?.Options.find(x => x.Value === groupById.Value)?.Display;
                        const queryString = subset.Value.split(",").reduce((q, x) =>
                        {
                            if (!x)
                            {
                                return q;
                            }
                            return q + ` "${groupBy}"="${subset.Options.find(v => v.Value === x)?.Display}"`;
                        }, "");
                        filter.push({
                            Key: "QueryString",
                            Value: queryString,
                        });
                    }
                    const parms: AppLinkParms = {
                        App: "glaccountdet",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms).then((executionResult: ExecutionResult<string>) =>
                    {
                        const index = executionResult.Result.indexOf("?");
                        if (executionResult.Result.includes("apps/form"))
                        {
                            this.$navigationService.navigateToApp(executionResult.Result);
                        }
                        else if (index > 0)
                        {
                            this.$intentService.resolve("glaccountdet", "Search", {
                                querystring: executionResult.Result.substring(index, executionResult.Result.length),
                            });
                        }
                    });
                },
            },
            data: {
                labels,
                datasets: [
                    {
                        label: `${this.ChartSubtitle} Budget`,
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: chartColors[0],
                        borderColor: chartColors[0],
                        hoverBackgroundColor: chartColors[0],
                        hoverBorderColor: chartColors[0],
                    },
                    {
                        label: `${this.ChartSubtitle} Activity`,
                        data: this.chartCriteria.ChartDatas[1].Data,
                        backgroundColor: chartColors[1],
                        borderColor: chartColors[1],
                        hoverBackgroundColor: chartColors[1],
                        hoverBorderColor: chartColors[1],
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
